import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Building2,
  CheckCircle,
  Clock,
  Play,
  Sparkles,
  Download,
  Loader2,
  Megaphone,
  Search,
  Send,
  MailCheck,
  AlertCircle,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { dashboardApi, scrapersApi, qualificationApi } from '@/services/api'
import { useOrgStore } from '@/stores/org-store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import SetupProgress from '@/components/setup-progress'

interface StatsData {
  total_companies: number
  qualified_companies: number
  pending_companies: number
  campaigns: number
  smart_searches: number
  outreach_ready: number
  sent: number
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { currentOrg } = useOrgStore()
  
  const { data: stats, isLoading, error, refetch } = useQuery<StatsData>({
    queryKey: ['dashboard-stats', currentOrg?.id],
    queryFn: dashboardApi.getStats,
    enabled: !!currentOrg?.id
  })

  const scrapeMutation = useMutation({
    mutationFn: () => scrapersApi.runAll(),
    onSuccess: () => {
      refetch()
      navigate('/jobs')
    }
  })

  const qualifyMutation = useMutation({
    mutationFn: () => qualificationApi.run(),
    onSuccess: () => {
      refetch()
      navigate('/jobs')
    }
  })

  const pipelineCards = [
    {
      name: 'Total Companies',
      value: stats?.total_companies || 0,
      icon: Building2,
      bg: 'bg-primary/10',
      fg: 'text-primary',
    },
    {
      name: 'Qualified',
      value: stats?.qualified_companies || 0,
      icon: CheckCircle,
      bg: 'bg-chart-1/10',
      fg: 'text-chart-1',
    },
    {
      name: 'Pending',
      value: stats?.pending_companies || 0,
      icon: Clock,
      bg: 'bg-chart-5/10',
      fg: 'text-chart-5',
    },
  ]

  const outreachCards = [
    {
      name: 'Campaigns',
      value: stats?.campaigns || 0,
      icon: Megaphone,
      bg: 'bg-chart-2/10',
      fg: 'text-chart-2',
    },
    {
      name: 'Smart Searches',
      value: stats?.smart_searches || 0,
      icon: Search,
      bg: 'bg-chart-4/10',
      fg: 'text-chart-4',
    },
    {
      name: 'Outreach Ready',
      value: stats?.outreach_ready || 0,
      icon: MailCheck,
      bg: 'bg-chart-5/10',
      fg: 'text-chart-5',
    },
    {
      name: 'Sent',
      value: stats?.sent || 0,
      icon: Send,
      bg: 'bg-chart-1/10',
      fg: 'text-chart-1',
    },
  ]

  const StatCard = ({
    stat,
  }: {
    stat: { name: string; value: number; icon: React.ElementType; bg: string; fg: string }
  }) => (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={cn('flex h-11 w-11 items-center justify-center rounded-lg', stat.bg)}>
            <stat.icon className={cn('h-5 w-5', stat.fg)} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-muted-foreground">{stat.name}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-16 mt-1" />
            ) : (
              <p className="text-2xl font-semibold tracking-tight text-card-foreground">
                {stat.value.toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Overview of your prospecting pipeline
        </p>
      </div>

      {/* Setup progress — hidden once all steps are completed */}
      <SetupProgress />

      {/* Error message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading dashboard stats: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Pipeline stats */}
      <div className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Pipeline</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pipelineCards.map((stat) => (
            <StatCard key={stat.name} stat={stat} />
          ))}
        </div>
      </div>

      {/* Outreach stats */}
      <div className="space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Outreach</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {outreachCards.map((stat) => (
            <StatCard key={stat.name} stat={stat} />
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
          <CardDescription>
            Run jobs and manage your prospecting workflow
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => scrapeMutation.mutate()}
              disabled={scrapeMutation.isPending}
            >
              {scrapeMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run Scrape Job
            </Button>
            <Button 
              variant="secondary"
              onClick={() => qualifyMutation.mutate()}
              disabled={qualifyMutation.isPending}
            >
              {qualifyMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Qualify Companies
            </Button>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export Data
            </Button>
          </div>

          {(scrapeMutation.isError || qualifyMutation.isError) && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {scrapeMutation.error?.message || qualifyMutation.error?.message || 'An error occurred'}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Placeholder for activity feed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
          <CardDescription>
            Latest updates from your prospecting pipeline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-muted-foreground">
              Activity feed coming soon...
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
